import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Typography, LinearProgress, Box, Button, Snackbar, Alert } from '@mui/material';
import { Storage } from 'aws-amplify';
import { v4 as uuidv4 } from 'uuid';
import * as React from 'react';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import ImageList from '@mui/material/ImageList';
import { TranslucentContainer } from '../../../../../components';
import CardAssetPhotoList from './CardAssetPhotoList';
import * as UserActions from '../../../../../core/users/action';
import { useIsMobile } from '../../../../../hooks';

export default function VendorPhotos() {
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const { user, loading } = useSelector((store) => store.users);

  const [imageUpload, setImageUpload] = useState();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [buffer, setBuffer] = useState(0);
  const [openSnack, setOpenSnack] = useState(false);
  const [snackMessage, setSnackMessage] = useState();
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  useEffect(() => {
    if (!isFirstLoad) return;
    setIsFirstLoad(false);
    dispatch(UserActions.getUserByCognitoId(user.cognito_id));
  }, [dispatch, user, isFirstLoad]);

  const handleAssetPhotoUpload = (e) => {
    if (!e.target.files) {
      return;
    }
    const file = e.target.files[0];
    // 10 MB === 10485760 Bytes
    if (file.size >= 10485760) {
      setSnackMessage('Photo exceeds the allowed size (10MB)');
      setOpenSnack(true);
      setImageUpload(false);
    } else if (user?.assets.length >= 10) {
      setSnackMessage('Max 10 Photos');
      setOpenSnack(true);
      setImageUpload(false);
    } else {
      setImageUpload(file);
    }
  };

  const progressCallback = (progressData) => {
    const normalise = ((progressData.loaded - 0) * 100) / (progressData.total - 0);
    const diff = Math.random() * 10;
    const diff2 = Math.random() * 10;

    setProgress(normalise + diff);
    setBuffer(normalise + diff + diff2);

    if (normalise === 100) setUploading(false);
  };

  const handleUploadAssetPhoto = async () => {
    setUploading(true);

    if (imageUpload) {
      try {
        const resultUpload = await Storage.put(
          `photo_asset_${user.cognito_id}_${uuidv4()}`,
          imageUpload,
          {
            metadata: { name: `${imageUpload.name}` },
            progressCallback,
          },
        );
        if (resultUpload.key !== '') {
          setImageUpload(false);
          dispatch(
            UserActions.createUserAsset({
              type_of_asset: 'photo',
              s3_object_key: resultUpload.key,
              fk_user: user.id,
            }),
          );
        } else {
          setSnackMessage('Problem uploading Photo, please try again!');
          setOpenSnack(true);
        }
      } catch (error) {
        console.log('Error uploading frontFile: ', error);
      }
    }
  };

  const handleCloseSnack = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnack(false);
    setSnackMessage('');
  };

  return (
    <Grid container justifyContent="center" textAlign="center">
      <TranslucentContainer>
        <Grid container m={2} alignItems="center">
          <Typography component="div" variant="h5" color="#0997BA" textAlign="left" mr={3}>
            Assets - Photos
          </Typography>
          {uploading && (
            <Box sx={{ width: '95%' }}>
              <LinearProgress variant="buffer" value={progress} valueBuffer={buffer} />
            </Box>
          )}
          <input
            type="file"
            id="asset-photo"
            name="asset-photo-upload"
            accept="image/png, image/jpeg"
            onChange={handleAssetPhotoUpload}
          />
          {imageUpload && (
            <Box textAlign="center">
              <Button
                variant="outlined"
                size="small"
                sx={{ mt: 1 }}
                onClick={() => handleUploadAssetPhoto()}>
                Upload Photo
              </Button>
            </Box>
          )}
        </Grid>
        {loading ? (
          <Grid container>
            <Stack spacing={1} p={3}>
              <Skeleton variant="rectangular" width={200} height={180} />
              <Skeleton variant="circular" width={40} height={40} />
            </Stack>
            <Stack spacing={1} p={3}>
              <Skeleton variant="rectangular" width={150} height={180} />
              <Skeleton variant="circular" width={40} height={40} />
            </Stack>
            <Stack spacing={1} p={3}>
              <Skeleton variant="rectangular" width={150} height={180} />
              <Skeleton variant="circular" width={40} height={40} />
            </Stack>
            <Stack spacing={1} p={3}>
              <Skeleton variant="rectangular" width={150} height={180} />
              <Skeleton variant="circular" width={40} height={40} />
            </Stack>
            <Stack spacing={1} p={3}>
              <Skeleton variant="rectangular" width={150} height={180} />
              <Skeleton variant="circular" width={40} height={40} />
            </Stack>
            <Stack spacing={1} p={3}>
              <Skeleton variant="rectangular" width={200} height={180} />
              <Skeleton variant="circular" width={40} height={40} />
            </Stack>
          </Grid>
        ) : (
          <Grid container>
            <ImageList variant="quilted" cols={isMobile ? 1 : 5} gap={8} sx={{ padding: 3 }}>
              {user?.assets?.map((item) => (
                <CardAssetPhotoList key={item.id} asset={item} />
              ))}
            </ImageList>
          </Grid>
        )}
      </TranslucentContainer>
      <Snackbar open={openSnack} autoHideDuration={5000} onClose={handleCloseSnack}>
        <Alert severity="warning">{snackMessage}</Alert>
      </Snackbar>
    </Grid>
  );
}
